import { observable, makeObservable, action } from 'mobx';
import { ValidationErrors, getChildErrors, getErrorsFor } from '~/lib/helper/ErrorHelper';
import { idGenerator } from '~/lib/helper/IdHelper';

const nameServerId = idGenerator('nameserver-');

// Mirrors NameServersDTO.MaxNameservers on the server (there is no cross-language constant).
// The server is authoritative; this only gates the "Add Nameserver" button.
export const MAX_NAMESERVERS = 13;

// Empty fields shown when a domain has fewer nameservers configured than this.
export const MIN_NAMESERVER_ROWS = 2;

// A single editable nameserver row. Errors live on the row object (not a positional map), so
// removing a row drops its error with it — no reindexing needed (see NameServersSection).
export class NameServerItem {
    id: string = nameServerId();

    @observable
    value: string;

    @observable
    errors: string[] = [];

    constructor(value = '') {
        this.value = value;
        makeObservable(this);
    }

    isEmpty() {
        return this.value.trim() === '';
    }

    @action
    setValue(value: string) {
        this.value = value;
        this.errors = [];
    }

    @action
    setErrors(errors: string[]) {
        this.errors = errors;
    }
}

export class NameServerConfiguration {
    @observable
    items: NameServerItem[] = [];

    // List-level (not per-row) errors, e.g. the min-1 / max-13 messages keyed `name_servers`.
    @observable
    listErrors: string[] = [];

    constructor() {
        makeObservable(this);
    }

    get canAdd(): boolean {
        return this.items.length < MAX_NAMESERVERS;
    }

    @action
    setItems(items: NameServerItem[]) {
        this.items = items;
    }

    @action
    deleteItem(idx: number) {
        this.items.splice(idx, 1);
    }

    @action
    addNewItem() {
        if (this.canAdd) this.items.push(new NameServerItem());
    }

    @action
    setListErrors(errors: string[]) {
        this.listErrors = errors;
    }

    clearErrors() {
        this.items.forEach((item) => {
            item.setErrors([]);
        });
        this.setListErrors([]);
    }

    // Loads the full stored list without truncation, padded with empty fields up to the display minimum.
    // A registrar-synced domain may hold more than the editor's cap; it must survive a round-trip and be
    // reducible, never silently clipped.
    setFromApi(nameServers: string[]) {
        const items = nameServers.map((ns) => new NameServerItem(ns));
        while (items.length < MIN_NAMESERVER_ROWS) {
            items.push(new NameServerItem());
        }
        this.setItems(items);
    }

    // Every row is submitted in order (including blanks); the server drops blanks but validates the rest
    // in place at these same indices, so the returned name_servers[i] errors line up with these rows.
    getApiValues(): string[] {
        return this.items.map((item) => item.value);
    }

    // `errors` are scoped to the nameserver list, keyed `name_servers[i]`: the update path passes the raw
    // errors; the registration path strips its outer `name_servers.` nesting first (getChildErrors). Errors
    // bind to the row object at index i, so a later row removal drops its error — no positional rebasing.
    setErrorsFromApiErrors(errors: ValidationErrors) {
        const indexed = getChildErrors(errors, 'name_servers'); // e.g. { '[0]': [...], '[1]': [...] }
        this.items.forEach((item, i) => {
            item.setErrors(indexed[`[${i}]`] ?? []);
        });
        this.setListErrors(getErrorsFor(errors, 'name_servers') ?? []);
    }
}
